import { useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { Sku } from "../api/models"
import { requiredMark, skuNameValid } from "./InstallerFields"
import { ImageFieldView, useImageField } from "./ImageField"
import { SKU_LOGO_FIELD_HINT, useSkuLogoField } from "./LogoPlacementDialog"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// Edit the sku's hero fields — name, description, highlights, code prefix,
// the screenshot and the logo (replace via presign→PUT like AddSkuDialog,
// or remove; a replacement logo goes through the placement editor).
// The name is freely editable — uploads are keyed by the sku's id, so a
// rename moves nothing. Sends a minimal diff patch and skips the call when
// nothing changed. Owner is write-once (the upload namespace).
// Conditionally mounted (OfferingDialog pattern).
export default function EditSkuDialog({ sku, hasReleasedVersions, accessToken, onClose, onSaved }: {
  sku: Sku
  // drives the rename heads-up: released versions' installer file names
  // keep the old name until a new release
  hasReleasedVersions: boolean
  accessToken: string
  onClose: () => void
  onSaved: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  const [name, setName] = useState(sku.name)
  const [description, setDescription] = useState(sku.description)
  const [highlightsText, setHighlightsText] = useState(sku.highlights.join("\n"))
  const [codePrefix, setCodePrefix] = useState(sku.codePrefix ?? "")
  const screenshot = useImageField(accessToken)
  const { logo, editor: logoEditor, adjust: adjustLogo } = useSkuLogoField({
    accessToken, ownerId: sku.ownerId, excludeSkuId: sku.id
  })
  // the sku's OWN logo — one borrowed from its only product (logoProductId)
  // is not the sku's to replace or remove
  const ownLogoUrl = sku.logoProductId === undefined ? sku.logoUrl : undefined
  // remove the existing image / logo on save (moot once a replacement is
  // staged)
  const [imageRemoved, setImageRemoved] = useState(false)
  const [logoRemoved, setLogoRemoved] = useState(false)
  const [step, setStep] = useState<"form" | "saving" | "error">("form")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const highlights = highlightsText.split("\n").map(h => h.trim()).filter(h => h !== "")
  const nameValid = skuNameValid(name)
  const renamed = name.trim() !== sku.name

  const formValid =
    nameValid
    && description.trim() !== ""
    && highlights.every(h => !h.includes(";"))
    && (codePrefix.trim() === "" || /^[A-Z0-9]{4}$/.test(codePrefix.trim()))

  const handleSubmit = async () => {
    if (!formValid) return
    setStep("saving")
    setErrorMessage("")
    try {
      const patch: Parameters<typeof Api.updateSku>[0]["patch"] = {}
      if (renamed) patch.name = name.trim()
      if (description.trim() !== sku.description) patch.description = description.trim()
      if (highlights.join("\n") !== sku.highlights.join("\n")) patch.highlights = highlights
      if ((codePrefix.trim() || null) !== (sku.codePrefix ?? null)) patch.codePrefix = codePrefix.trim() || null
      if (screenshot.file) {
        const { width, rev } = await screenshot.upload(sku.id)
        patch.imageWidth = width
        patch.imageRev = rev
      } else if (imageRemoved && sku.imageUrl) {
        patch.imageWidth = null
      }
      if (logo.file) {
        const { width, rev } = await logo.upload(sku.id)
        patch.logoWidth = width
        patch.logoRev = rev
      } else if (logoRemoved && ownLogoUrl) {
        patch.logoWidth = null
      }
      if (Object.keys(patch).length > 0)
        await Api.updateSku({ skuId: sku.id, patch, accessToken })
      onSaved()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  const busy = step === "saving"

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content no-scrollbar" style={{ maxHeight: "85vh", overflowY: "auto" }}>
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">{sku.name}</h2>

        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Name{requiredMark}</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="generate-codes-input"
              disabled={busy}
            />
            {/* one slot, swapped in place: name error > rename heads-up >
                reassurance */}
            <span style={{
              fontSize: "9pt",
              minHeight: "1.3em",
              color: !nameValid ? "#e07470"
                : renamed && hasReleasedVersions ? "var(--nfa-yellow)" : "var(--nfa-light-grey)"
            }}>
              {!nameValid
                ? "The name must contain at least one alphanumeric character."
                : renamed && hasReleasedVersions
                  ? "Released installers keep their current file names until the next release."
                  : "Renaming is safe: uploaded files and download links are not tied to the name."}
            </span>
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="generate-codes-input"
              disabled={busy}
              rows={3}
              autoFocus
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Highlights</span>
            <textarea
              value={highlightsText}
              onChange={(e) => setHighlightsText(e.target.value)}
              className="generate-codes-input"
              disabled={busy}
              rows={3}
              placeholder={"one highlight per line"}
            />
          </label>

          <label className="generate-codes-field">
            <span className="generate-codes-label">Code prefix</span>
            <input
              type="text"
              value={codePrefix}
              onChange={(e) => setCodePrefix(e.target.value.toUpperCase())}
              className="generate-codes-input"
              disabled={busy}
              maxLength={4}
              placeholder="empty: redemption codes cannot be minted"
            />
            {/* one fixed-height slot: the info line swaps to the error in
                place, so the dialog doesn't grow when it appears */}
            <span style={{
              fontSize: "9pt",
              minHeight: "2.6em",
              color: codePrefix.trim() !== "" && !/^[A-Z0-9]{4}$/.test(codePrefix.trim())
                ? "#e07470" : "var(--nfa-light-grey)"
            }}>
              {codePrefix.trim() !== "" && !/^[A-Z0-9]{4}$/.test(codePrefix.trim())
                ? "The prefix must be exactly 4 uppercase letters/digits — or empty."
                : "Exactly 4 uppercase letters/digits. Already-generated codes keep working under the old prefix; only future codes use the new one."}
            </span>
          </label>

          <ImageFieldView
            fields={screenshot}
            busy={busy}
            current={{
              imageUrl: sku.imageUrl,
              removed: imageRemoved,
              onRemove: () => setImageRemoved(true),
              onRestore: () => setImageRemoved(false)
            }}
          />
          <ImageFieldView
            fields={logo}
            busy={busy}
            label="Logo (PNG, optional)"
            hint={sku.logoProductId !== undefined
              ? `Currently showing the logo of this SKU's only product. ${SKU_LOGO_FIELD_HINT}`
              : SKU_LOGO_FIELD_HINT}
            variant="logo"
            onAdjust={adjustLogo}
            current={{
              imageUrl: ownLogoUrl,
              removed: logoRemoved,
              onRemove: () => setLogoRemoved(true),
              onRestore: () => setLogoRemoved(false)
            }}
          />
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={!formValid || busy}
            onClick={handleSubmit}
          >
            {busy ? <><Spinner size={12} /> Saving…</> : "Save"}
          </button>
        </div>
      </div>
      {logoEditor}
    </dialog>
  )
}

// Permanently delete a sku. Only possible when nothing references it — the
// backend 409s with the list of blockers (owners incl. revoked grants,
// orders, subscriptions, codes, reward purchases, offerings) and that
// message is surfaced here. Contents and version history are deleted with
// it; uploaded files stay on the buckets. Conditionally mounted.
export function DeleteSkuDialog({ sku, accessToken, onClose, onDeleted }: {
  sku: Sku
  accessToken: string
  onClose: () => void
  onDeleted: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"confirm" | "deleting" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleDelete = async () => {
    setStep("deleting")
    setErrorMessage("")
    try {
      await Api.deleteSku({ skuId: sku.id, accessToken })
      onDeleted()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "deleting") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Delete SKU</h2>

        <p>
          Permanently delete {sku.name}? Its contents and version history are
          deleted with it. Uploaded images and installers stay on the
          downloads bucket.
        </p>
        <p>
          This only works for a SKU nothing references: no owners (including
          revoked grants), no orders or subscriptions, no generated codes, no
          reward purchases and no offerings (including discontinued ones).
          Otherwise the SKU is kept and the blockers will be listed below.
        </p>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "deleting"}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={step === "deleting"}
            onClick={handleDelete}
          >
            {step === "deleting" ? <><Spinner size={12} /> Trying…</> : "Try Deleting"}
          </button>
        </div>
      </div>
    </dialog>
  )
}
